/**
 * Error Handler Service
 * Enhanced error handling and retry logic with exponential backoff
 */

export enum ErrorType {
  AUTHENTICATION_ERROR = 'auth_error',
  RATE_LIMIT_ERROR = 'rate_limit_error',
  NETWORK_ERROR = 'network_error',
  TIMEOUT_ERROR = 'timeout_error',
  DATA_VALIDATION_ERROR = 'validation_error',
  CONFLICT_ERROR = 'conflict_error',
  QUOTA_EXCEEDED = 'quota_exceeded',
  SERVICE_UNAVAILABLE = 'service_unavailable',
  UNKNOWN_ERROR = 'unknown_error'
}

export interface IntegrationError extends Error {
  type: ErrorType;
  code: string;
  statusCode?: number;
  retryable: boolean;
  retryAfter?: number;
  context?: Record<string, any>;
  originalError?: Error;
}

export interface ErrorResolution {
  action: 'retry' | 'escalate' | 'ignore' | 'user_action_required';
  delay?: number;
  maxRetries?: number;
  userMessage?: string;
  technicalDetails?: string;
}

export interface RetryConfig {
  maxAttempts: number;
  baseDelay: number;
  maxDelay: number;
  backoffMultiplier: number;
  jitterEnabled: boolean;
}

export class ErrorHandler {
  private retryConfig: RetryConfig;
  private errorStats: Map<string, ErrorStats> = new Map();

  constructor(retryConfig?: Partial<RetryConfig>) {
    this.retryConfig = {
      maxAttempts: 5,
      baseDelay: 1000, // 1 second
      maxDelay: 300000, // 5 minutes
      backoffMultiplier: 2,
      jitterEnabled: true,
      ...retryConfig
    };
  }

  /**
   * Handle an error and determine the appropriate resolution
   */
  async handleError(error: Error, requestId?: string): Promise<IntegrationError> {
    const integrationError = this.classifyError(error);
    
    // Record error statistics
    this.recordErrorStats(integrationError);

    // Log error details
    if (requestId) {
      console.error(`[${requestId}] Error handled:`, {
        type: integrationError.type,
        code: integrationError.code,
        message: integrationError.message,
        statusCode: integrationError.statusCode,
        retryable: integrationError.retryable
      });
    }

    return integrationError;
  }

  /**
   * Classify an error into an IntegrationError
   */
  private classifyError(error: Error): IntegrationError {
    // Handle fetch/network errors
    if (error.name === 'AbortError' || error.message.includes('timeout')) {
      return this.createIntegrationError(
        error,
        ErrorType.TIMEOUT_ERROR,
        'REQUEST_TIMEOUT',
        true,
        408
      );
    }

    if (error.message.includes('fetch') || error.message.includes('network')) {
      return this.createIntegrationError(
        error,
        ErrorType.NETWORK_ERROR,
        'NETWORK_FAILURE',
        true,
        0
      );
    }

    // Handle HTTP status code errors
    if (error.message.includes('401') || error.message.includes('Unauthorized')) {
      return this.createIntegrationError(
        error,
        ErrorType.AUTHENTICATION_ERROR,
        'UNAUTHORIZED',
        false,
        401
      );
    }

    if (error.message.includes('403') || error.message.includes('Forbidden')) {
      return this.createIntegrationError(
        error,
        ErrorType.AUTHENTICATION_ERROR,
        'FORBIDDEN',
        false,
        403
      );
    }

    if (error.message.includes('429') || error.message.includes('rate limit')) {
      const retryAfter = this.extractRetryAfter(error.message);
      return this.createIntegrationError(
        error,
        ErrorType.RATE_LIMIT_ERROR,
        'RATE_LIMITED',
        true,
        429,
        retryAfter
      );
    }

    if (error.message.includes('500') || error.message.includes('Internal Server Error')) {
      return this.createIntegrationError(
        error,
        ErrorType.SERVICE_UNAVAILABLE,
        'INTERNAL_SERVER_ERROR',
        true,
        500
      );
    }

    if (error.message.includes('502') || error.message.includes('Bad Gateway')) {
      return this.createIntegrationError(
        error,
        ErrorType.SERVICE_UNAVAILABLE,
        'BAD_GATEWAY',
        true,
        502
      );
    }

    if (error.message.includes('503') || error.message.includes('Service Unavailable')) {
      return this.createIntegrationError(
        error,
        ErrorType.SERVICE_UNAVAILABLE,
        'SERVICE_UNAVAILABLE',
        true,
        503
      );
    }

    if (error.message.includes('504') || error.message.includes('Gateway Timeout')) {
      return this.createIntegrationError(
        error,
        ErrorType.TIMEOUT_ERROR,
        'GATEWAY_TIMEOUT',
        true,
        504
      );
    }

    // Handle validation errors
    if (error.message.includes('validation') || error.message.includes('invalid')) {
      return this.createIntegrationError(
        error,
        ErrorType.DATA_VALIDATION_ERROR,
        'VALIDATION_FAILED',
        false,
        400
      );
    }

    // Handle quota errors
    if (error.message.includes('quota') || error.message.includes('limit exceeded')) {
      return this.createIntegrationError(
        error,
        ErrorType.QUOTA_EXCEEDED,
        'QUOTA_EXCEEDED',
        false,
        429
      );
    }

    // Default to unknown error
    return this.createIntegrationError(
      error,
      ErrorType.UNKNOWN_ERROR,
      'UNKNOWN',
      false,
      0
    );
  }

  /**
   * Create an IntegrationError from a base error
   */
  private createIntegrationError(
    originalError: Error,
    type: ErrorType,
    code: string,
    retryable: boolean,
    statusCode?: number,
    retryAfter?: number
  ): IntegrationError {
    const integrationError = new Error(originalError.message) as IntegrationError;
    integrationError.name = 'IntegrationError';
    integrationError.type = type;
    integrationError.code = code;
    integrationError.retryable = retryable;
    integrationError.statusCode = statusCode;
    integrationError.retryAfter = retryAfter;
    integrationError.originalError = originalError;
    integrationError.context = this.extractErrorContext(originalError);

    return integrationError;
  }

  /**
   * Extract retry-after value from error message
   */
  private extractRetryAfter(message: string): number | undefined {
    const match = message.match(/retry.*?(\d+)/i);
    return match ? parseInt(match[1], 10) : undefined;
  }

  /**
   * Extract additional context from error
   */
  private extractErrorContext(error: Error): Record<string, any> {
    const context: Record<string, any> = {
      timestamp: new Date().toISOString(),
      stack: error.stack
    };

    // Extract additional properties if they exist
    const errorObj = error as any;
    if (errorObj.response) {
      context.response = {
        status: errorObj.response.status,
        statusText: errorObj.response.statusText,
        headers: errorObj.response.headers
      };
    }

    if (errorObj.request) {
      context.request = {
        url: errorObj.request.url,
        method: errorObj.request.method
      };
    }

    return context;
  }

  /**
   * Check if an error is retryable
   */
  isRetryableError(error: Error): boolean {
    if (error.name === 'IntegrationError') {
      return (error as IntegrationError).retryable;
    }

    // Classify the error to determine if it's retryable
    const integrationError = this.classifyError(error);
    return integrationError.retryable;
  }

  /**
   * Calculate retry delay with exponential backoff
   */
  calculateRetryDelay(attemptNumber: number, error?: IntegrationError): number {
    // Use retry-after header if available
    if (error?.retryAfter) {
      return error.retryAfter * 1000; // Convert to milliseconds
    }

    // Calculate exponential backoff
    let delay = this.retryConfig.baseDelay * Math.pow(this.retryConfig.backoffMultiplier, attemptNumber - 1);
    
    // Apply maximum delay limit
    delay = Math.min(delay, this.retryConfig.maxDelay);

    // Add jitter to prevent thundering herd
    if (this.retryConfig.jitterEnabled) {
      const jitter = Math.random() * 0.1 * delay; // ±10% jitter
      delay += (Math.random() > 0.5 ? jitter : -jitter);
    }

    return Math.max(delay, 0);
  }

  /**
   * Get error resolution strategy
   */
  getErrorResolution(error: IntegrationError, attemptNumber: number): ErrorResolution {
    // Check if max retries exceeded
    if (attemptNumber >= this.retryConfig.maxAttempts) {
      return {
        action: 'escalate',
        userMessage: 'Maximum retry attempts exceeded. Please contact support.',
        technicalDetails: `Failed after ${attemptNumber} attempts: ${error.message}`
      };
    }

    // Handle different error types
    switch (error.type) {
      case ErrorType.AUTHENTICATION_ERROR:
        return {
          action: 'user_action_required',
          userMessage: 'Authentication failed. Please check your credentials.',
          technicalDetails: error.message
        };

      case ErrorType.RATE_LIMIT_ERROR:
        return {
          action: 'retry',
          delay: this.calculateRetryDelay(attemptNumber, error),
          maxRetries: this.retryConfig.maxAttempts,
          userMessage: 'Rate limit exceeded. Retrying automatically.',
          technicalDetails: `Rate limited. Retry after ${error.retryAfter || 'calculated delay'} seconds.`
        };

      case ErrorType.NETWORK_ERROR:
      case ErrorType.TIMEOUT_ERROR:
      case ErrorType.SERVICE_UNAVAILABLE:
        return {
          action: 'retry',
          delay: this.calculateRetryDelay(attemptNumber, error),
          maxRetries: this.retryConfig.maxAttempts,
          userMessage: 'Temporary service issue. Retrying automatically.',
          technicalDetails: error.message
        };

      case ErrorType.DATA_VALIDATION_ERROR:
        return {
          action: 'user_action_required',
          userMessage: 'Data validation failed. Please check your input.',
          technicalDetails: error.message
        };

      case ErrorType.QUOTA_EXCEEDED:
        return {
          action: 'escalate',
          userMessage: 'API quota exceeded. Please upgrade your plan or wait for quota reset.',
          technicalDetails: error.message
        };

      default:
        return {
          action: 'escalate',
          userMessage: 'An unexpected error occurred. Please contact support.',
          technicalDetails: error.message
        };
    }
  }

  /**
   * Record error statistics
   */
  private recordErrorStats(error: IntegrationError): void {
    const key = `${error.type}:${error.code}`;
    const stats = this.errorStats.get(key) || {
      count: 0,
      firstOccurrence: new Date(),
      lastOccurrence: new Date(),
      type: error.type,
      code: error.code
    };

    stats.count++;
    stats.lastOccurrence = new Date();
    this.errorStats.set(key, stats);
  }

  /**
   * Get error statistics
   */
  getErrorStats(): ErrorStats[] {
    return Array.from(this.errorStats.values());
  }

  /**
   * Clear error statistics
   */
  clearErrorStats(): void {
    this.errorStats.clear();
  }

  /**
   * Create a circuit breaker for error handling
   */
  createCircuitBreaker(config?: CircuitBreakerConfig): CircuitBreaker {
    return new CircuitBreaker(config);
  }
}

interface ErrorStats {
  count: number;
  firstOccurrence: Date;
  lastOccurrence: Date;
  type: ErrorType;
  code: string;
}

interface CircuitBreakerConfig {
  failureThreshold: number;
  recoveryTimeout: number;
  monitoringPeriod: number;
}

export class CircuitBreaker {
  private state: 'closed' | 'open' | 'half_open' = 'closed';
  private failureCount: number = 0;
  private lastFailureTime?: Date;
  private config: CircuitBreakerConfig;

  constructor(config?: Partial<CircuitBreakerConfig>) {
    this.config = {
      failureThreshold: 5,
      recoveryTimeout: 60000, // 1 minute
      monitoringPeriod: 10000, // 10 seconds
      ...config
    };
  }

  async execute<T>(operation: () => Promise<T>): Promise<T> {
    if (this.state === 'open') {
      if (this.shouldAttemptReset()) {
        this.state = 'half_open';
      } else {
        throw new Error('Circuit breaker is open');
      }
    }

    try {
      const result = await operation();
      this.onSuccess();
      return result;
    } catch (error) {
      this.onFailure();
      throw error;
    }
  }

  private shouldAttemptReset(): boolean {
    return this.lastFailureTime && 
           Date.now() - this.lastFailureTime.getTime() > this.config.recoveryTimeout;
  }

  private onSuccess(): void {
    this.failureCount = 0;
    this.state = 'closed';
  }

  private onFailure(): void {
    this.failureCount++;
    this.lastFailureTime = new Date();

    if (this.failureCount >= this.config.failureThreshold) {
      this.state = 'open';
    }
  }

  getState(): string {
    return this.state;
  }

  getFailureCount(): number {
    return this.failureCount;
  }
}